import { React, useState, useEffect, useContext } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import Table from "react-bootstrap/Table";
import Alert from "react-bootstrap/Alert";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { AuthConsumer, AuthContext } from "../helpers/AuthContext";
import "../styles/responsive.css";

import "react-circular-progressbar/dist/styles.css";

const Dashboard = () => {
  const auth = useContext(AuthContext);
  //User variable
  const [user, setUser] = useState(null);
  const [rank, setRank] = useState("NA");
  const [progress, setProgress] = useState(0);

  // Lab-prompt banner: shown to a logged-in user who hasn't set a lab yet
  // (and hasn't chosen "I'm not in a lab"). Lets them join their lab's
  // competition in one click without opening the full profile form.
  const [labOptions, setLabOptions] = useState([]);
  const [typedLab, setTypedLab] = useState("");
  const [savingLab, setSavingLab] = useState(false);
  const [labMsg, setLabMsg] = useState("");
  const [labDismissed, setLabDismissed] = useState(() => {
    try {
      return localStorage.getItem("labNoticeDismissed") === "1";
    } catch (_) {
      return false;
    }
  });

  //Get user data
  const getUserData = () => {
    const email = localStorage.getItem("Email");

    if (!email || email === "guest") {
      return;
    }

    axios
      .get("/api/users/user", {
        params: {
          email,
        },
      })
      .then((res) => {
        setUser(res.data);
      })
      .catch((err) => {
        // console.log(err);
      });
  };

  // Save the typed lab (from the prompt banner) via the lightweight endpoint.
  const saveLab = () => {
    const lab = typedLab.trim();
    if (!lab) {
      setLabMsg("Type your lab name, or choose “I'm not in a lab”.");
      return;
    }
    setSavingLab(true);
    setLabMsg("");
    axios
      .post("/api/users/set-lab", { lab })
      .then((res) => {
        const saved = (res.data && res.data.lab) || lab;
        setUser((u) => (u ? { ...u, lab: saved } : u)); // hides the banner
        // Update shared auth state (patches Info + clears the navbar dot).
        if (auth && auth.updateLab) auth.updateLab(saved);
      })
      .catch((e) => {
        setLabMsg(e?.response?.data?.message || "Couldn't save. Try again.");
      })
      .finally(() => setSavingLab(false));
  };

  // "I'm not in a lab" — stop asking (per device). Also clears the navbar dot.
  const dismissLab = () => {
    setLabDismissed(true);
    if (auth && auth.dismissLabNotice) auth.dismissLabNotice();
    else {
      try {
        localStorage.setItem("labNoticeDismissed", "1");
      } catch (_) {}
    }
  };

  const renderLabPrompt = () => {
    if (!user || (user.lab && user.lab.trim()) || labDismissed) return null;
    return (
      <div
        style={{
          marginBottom: 10,
          padding: "14px 16px",
          borderRadius: 12,
          border: "1px solid rgba(8,145,178,0.3)",
          background: "rgba(8,145,178,0.06)",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div style={{ flex: "1 1 260px", minWidth: 220 }}>
          <div style={{ fontWeight: 700, color: "#0f172a" }}>
            🔬 Which lab are you in?
          </div>
          <div style={{ fontSize: 13, color: "#475569", marginTop: 2 }}>
            Set your research lab so your labels count toward your lab in the
            competition. Pick it from the list if it's already there.
          </div>
        </div>
        <input
          list="dash-lab-options"
          style={{
            flex: "1 1 200px",
            minWidth: 180,
            padding: "9px 12px",
            borderRadius: 8,
            border: "1px solid #cbd5e1",
            fontSize: 14,
          }}
          placeholder="Start typing your lab…"
          value={typedLab}
          onChange={(e) => setTypedLab(e.target.value)}
          disabled={savingLab}
        />
        <datalist id="dash-lab-options">
          {labOptions.map((l) => (
            <option key={l} value={l} />
          ))}
        </datalist>
        <button
          type="button"
          onClick={saveLab}
          disabled={savingLab}
          style={{
            padding: "9px 16px",
            borderRadius: 8,
            border: "none",
            background: "#0891b2",
            color: "#fff",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {savingLab ? "Saving…" : "Save lab"}
        </button>
        <button
          type="button"
          onClick={dismissLab}
          disabled={savingLab}
          style={{
            padding: "9px 14px",
            borderRadius: 8,
            border: "1px solid #cbd5e1",
            background: "#fff",
            color: "#475569",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          I'm not in a lab
        </button>
        {labMsg && (
          <div style={{ flexBasis: "100%", fontSize: 13, color: "#b91c1c" }}>
            {labMsg}
          </div>
        )}
      </div>
    );
  };

  //await until user data is set, to set rank and progress
  useEffect(() => {
    if (!user) {
      return;
    }

    const comp = user.components.length;
    let perc = 0;

    if (comp === 0) {
      setRank("Newbie");
      perc = 0;
    } else if (comp <= 5) {
      setRank("Rookie");
      perc = Math.floor((comp / 5.0) * 100);
    } else if (comp < 10) {
      setRank("Beginner");
      perc = Math.floor(((comp - 6) / 4.0) * 100);
    } else if (comp < 25) {
      setRank("Intermediate");
      perc = Math.floor(((comp - 10) / 15.0) * 100);
    } else if (comp < 30) {
      setRank("Seasoned");
      perc = Math.floor(((comp - 25) / 5.0) * 100);
    } else if (comp < 50) {
      setRank("Proficient");
      perc = Math.floor(((comp - 30) / 20.0) * 100);
    } else if (comp < 75) {
      setRank("Experienced");
      perc = Math.floor(((comp - 50) / 25.0) * 100);
    } else if (comp < 100) {
      setRank("Advanced");
      perc = Math.floor(((comp - 75) / 25.0) * 100);
    } else if (comp < 500) {
      setRank("Expert");
      perc = Math.floor(((comp - 100) / 400.0) * 100);
    } else if (comp >= 500) {
      setRank("Ultimate Master");
      perc = 100;
    } else {
      setRank("You Don't Exist");
      perc = 100;
    }

    setProgress(perc);
  }, [user]);

  //on page reload use effect
  useEffect(() => {
    //Get user data
    getUserData();
    // Existing lab names to suggest in the prompt banner.
    axios
      .get("/api/users/labs")
      .then((res) => setLabOptions((res.data && res.data.labs) || []))
      .catch(() => {});
  }, []);

  return (
    <AuthConsumer>
      {({ Auth, Name }) => {
        //If admin
        if (Auth === "Admin") {
          return (
            <div>
              <Sidebar />
              <div className="content-with-sidebar">
                <Container
                  className="mobile-padding-sm"
                  style={{ padding: "10px" }}
                >
                  <Card style={{ marginBottom: "10px" }}>
                    <Card.Header as="h2">
                      {Name} <Badge bg="secondary">{rank}</Badge>
                    </Card.Header>
                  </Card>
                  {renderLabPrompt()}
                  <Row>
                    <Col xs={12} lg={6} className="mb-3 mb-lg-0">
                      <Card>
                        <Card.Header as="h5">Profile</Card.Header>
                        <Card.Body>
                          <Container className="mobile-padding-sm">
                            {user ? (
                              <div className="table-responsive-mobile">
                                <Table hover>
                                  <tbody>
                                    <tr>
                                      <td>First Name</td>
                                      <td>{user.fname}</td>
                                    </tr>
                                    <tr>
                                      <td>Last Name</td>
                                      <td>{user.lname}</td>
                                    </tr>
                                    <tr>
                                      <td>Email</td>
                                      <td style={{ wordBreak: "break-all" }}>
                                        {user.email}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Association</td>
                                      <td>{user.assoc}</td>
                                    </tr>
                                    <tr>
                                      <td>Education</td>
                                      <td>{user.edu}</td>
                                    </tr>
                                    <tr>
                                      <td>Experience</td>
                                      <td>{user.exp}</td>
                                    </tr>
                                  </tbody>
                                </Table>
                              </div>
                            ) : (
                              <Alert key="noprofile" variant="warning">
                                No User Profile Retrieved
                              </Alert>
                            )}
                          </Container>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col xs={12} lg={6}>
                      <Card>
                        <Card.Header as="h5">
                          Progress To Next Level
                        </Card.Header>
                        <Card.Body>
                          {user ? (
                            <Container className="mobile-padding-sm">
                              <Container
                                style={{
                                  width: "50%",
                                  maxWidth: "200px",
                                  margin: "0 auto",
                                }}
                              >
                                <CircularProgressbar
                                  value={progress}
                                  text={`${progress}%`}
                                  strokeWidth={5}
                                  styles={buildStyles({
                                    strokeLinecap: "butt",
                                  })}
                                />
                              </Container>
                              <Container style={{ paddingTop: "30px" }}>
                                <div className="table-responsive-mobile">
                                  <Table hover>
                                    <tbody>
                                      <tr>
                                        <td>Skill Level</td>
                                        <td>
                                          <Badge bg="secondary">{rank}</Badge>
                                        </td>
                                      </tr>
                                      <tr>
                                        <td>Components Labeled</td>
                                        <td>{user.components.length}</td>
                                      </tr>
                                    </tbody>
                                  </Table>
                                </div>
                              </Container>
                            </Container>
                          ) : (
                            <Container className="mobile-padding-sm">
                              <Alert key="noprogress" variant="warning">
                                No User Progress Retrieved
                              </Alert>
                            </Container>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                  <Card style={{ marginTop: "10px" }}>
                    <Card.Header>Admin Links</Card.Header>
                    <Card.Body>
                      <p>Hey, you are authenticated as an admin.</p>
                    </Card.Body>
                  </Card>
                </Container>
              </div>
            </div>
          );
        }

        //If logged in user
        else if (Auth === "User") {
          return (
            <div>
              <Sidebar />
              <div className="content-with-sidebar">
                <Container
                  className="mobile-padding-sm"
                  style={{ padding: "10px" }}
                >
                  <Card style={{ marginBottom: "10px" }}>
                    <Card.Header as="h2">
                      {Name} <Badge bg="secondary">{rank}</Badge>
                    </Card.Header>
                  </Card>
                  {renderLabPrompt()}
                  <Row>
                    <Col xs={12} lg={6} className="mb-3 mb-lg-0">
                      <Card>
                        <Card.Header as="h5">Profile</Card.Header>
                        <Card.Body>
                          <Container className="mobile-padding-sm">
                            {user ? (
                              <div className="table-responsive-mobile">
                                <Table hover>
                                  <tbody>
                                    <tr>
                                      <td>First Name</td>
                                      <td>{user.fname}</td>
                                    </tr>
                                    <tr>
                                      <td>Last Name</td>
                                      <td>{user.lname}</td>
                                    </tr>
                                    <tr>
                                      <td>Email</td>
                                      <td style={{ wordBreak: "break-all" }}>
                                        {user.email}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Association</td>
                                      <td>{user.assoc}</td>
                                    </tr>
                                    <tr>
                                      <td>Education</td>
                                      <td>{user.edu}</td>
                                    </tr>
                                    <tr>
                                      <td>Experience</td>
                                      <td>{user.exp}</td>
                                    </tr>
                                  </tbody>
                                </Table>
                              </div>
                            ) : (
                              <Alert key="noprofile" variant="warning">
                                No User Profile Retrieved
                              </Alert>
                            )}
                          </Container>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col xs={12} lg={6}>
                      <Card>
                        <Card.Header as="h5">
                          Progress To Next Level
                        </Card.Header>
                        <Card.Body>
                          {user ? (
                            <Container className="mobile-padding-sm">
                              <Container
                                style={{
                                  width: "50%",
                                  maxWidth: "200px",
                                  margin: "0 auto",
                                }}
                              >
                                <CircularProgressbar
                                  value={progress}
                                  text={`${progress}%`}
                                  strokeWidth={5}
                                  styles={buildStyles({
                                    strokeLinecap: "butt",
                                  })}
                                />
                              </Container>
                              <Container style={{ paddingTop: "30px" }}>
                                <div className="table-responsive-mobile">
                                  <Table hover>
                                    <tbody>
                                      <tr>
                                        <td>Skill Level</td>
                                        <td>
                                          <Badge bg="secondary">{rank}</Badge>
                                        </td>
                                      </tr>
                                      <tr>
                                        <td>Components Labeled</td>
                                        <td>{user.components.length}</td>
                                      </tr>
                                    </tbody>
                                  </Table>
                                </div>
                              </Container>
                            </Container>
                          ) : (
                            <Container className="mobile-padding-sm">
                              <Alert key="noprogress" variant="warning">
                                No User Progress Retrieved
                              </Alert>
                            </Container>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Container>
              </div>
            </div>
          );
        }

        //If guest
        else {
          return (
            <div>
              <Sidebar />
              <div className="content-with-sidebar">
                <Container
                  className="mobile-padding-sm"
                  style={{ padding: "10px" }}
                >
                  <Card>
                    <Card.Header as="h3">
                      Sorry, looks like you are in the wrong place?
                    </Card.Header>
                    <Card.Body>
                      <Row style={{ padding: "5px" }}>
                        <p>You are not authenticated to view the page.</p>
                      </Row>
                      <Row style={{ padding: "5px" }}>
                        <p>
                          Please <a href="/login">login</a> with to access the
                          page.
                        </p>
                      </Row>
                    </Card.Body>
                  </Card>
                </Container>
              </div>
            </div>
          );
        }
      }}
    </AuthConsumer>
  );
};

export default Dashboard;
